import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Button, ErrorResponse } from '~components';
import { TableView } from '~components/table-view-v3';
import useEditableTableRows, { EditableRow } from '~support-at-home/hooks/useEditableTableRows';

import columns from '~support-at-home/forms/sah-budgets/listSingle';

import { create, deleteOne, getOne, update } from '~support-at-home/actions/sahBudgets';

import { selectSahBudgetsByClient } from '~support-at-home/selectors/sahBudgets';

import { filterBudgetRowFields } from '../helpers';

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const BudgetsModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  const clientBudgets = useAppSelector(selectSahBudgetsByClient);

  const persistedBudgets: EditableRow[] = useMemo(
    () => clientBudgets.map((budget) => ({ ...budget, persisted: true, backendId: budget.id })),
    [clientBudgets],
  );

  const getDefaultRow = useCallback(
    (): EditableRow => ({
      id: crypto.randomUUID(),
      client_id: id,
      sah_budget_id: '',
      origin_tag: 'manual',
      start_date: '',
      end_date: '',
    }),
    [id],
  );

  const load = useCallback(() => {
    dispatch(getOne({ client_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (isOpen && id !== null) {
      load();
    }
  }, [id, isOpen, load]);

  const { rows, tableRef, error, isProcessing, handleChange, canRowValidate, confirmChanges } = useEditableTableRows({
    source: persistedBudgets,
    getDefaultRow,
    onCreate: async (row) => {
      const toCreate = filterBudgetRowFields(row, columns, id, row.sah_budget_id);
      await dispatch(create(toCreate)).unwrap();
    },
    onUpdate: async (row) => {
      if (!row.backendId) return;
      const toUpdate = filterBudgetRowFields(row, columns, id);
      await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
    },
    onDelete: async (row) => {
      if (!row.backendId) return;
      await dispatch(deleteOne({ id: row.backendId })).unwrap();
    },
  });

  const handleConfirm = async () => {
    const outcome = await confirmChanges();
    if (outcome === 'saved') {
      load();
      onClose(true);
    } else if (outcome === 'clean') {
      onClose(false);
    }
  };

  return (
    <Modal isOpen={isOpen} size="xl" style={{ maxWidth: '1500px', width: '100%' }} data-cy="client-budgets-modal">
      <ModalHeader className="bg-success text-white">Client SAH Budgets</ModalHeader>
      <ModalBody>
        <ErrorResponse message={error} dataCy="client-budgets-modal-error" />
        <TableView
          ref={tableRef}
          id="client-budgets-modal"
          columns={columns}
          data={rows}
          editonly
          onChange={handleChange}
          canRowValidate={canRowValidate}
          canDeleteRow
          showAddRow
          emptyMessage="No data found"
          bordered
          compactView
        />
      </ModalBody>
      <ModalFooter className="justify-content-end">
        <Button
          size="sm"
          color="success"
          className="ps-4 pe-4 mx-2"
          onClick={handleConfirm}
          loading={isProcessing}
          dataCy="client-budgets-modal-confirm-button"
        >
          Confirm
        </Button>
        <Button
          size="sm"
          className="ps-4 pe-4 mx-2"
          onClick={() => onClose(false)}
          disabled={isProcessing}
          dataCy="client-budgets-modal-cancel-button"
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BudgetsModal;
